import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card } from '@/components/ui/Card';

interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  role: string;
  emailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
}

async function fetchProfile(): Promise<UserProfile> {
  const token = useAuthStore.getState().accessToken;
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/users/me`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  if (!response.ok) throw new Error('Failed to fetch profile');
  return response.json();
}

async function updateProfile(data: { displayName: string; avatarUrl?: string | null }) {
  const token = useAuthStore.getState().accessToken;
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/users/me`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to update profile');
  return response.json();
}

export function ProfilePage() {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');

  // Fetch user profile
  const { data: profile, isLoading } = useQuery<UserProfile>({
    queryKey: ['profile'],
    queryFn: fetchProfile,
  });

  // Initialize form when profile loads
  useState(() => {
    if (profile) {
      setDisplayName(profile.displayName);
      setAvatarUrl(profile.avatarUrl || '');
    }
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setIsEditing(false);
    },
  });

  const handleSave = () => {
    updateProfileMutation.mutate({
      displayName,
      avatarUrl: avatarUrl || null,
    });
  };

  const handleCancel = () => {
    if (profile) {
      setDisplayName(profile.displayName);
      setAvatarUrl(profile.avatarUrl || '');
    }
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-4xl py-8">
        <div className="flex items-center justify-center p-8">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl py-8">
      <h1 className="mb-8 text-3xl font-bold">Min profil</h1>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Profile Card */}
        <Card className="md:col-span-1">
          <div className="p-6 text-center">
            <div className="mb-4 flex justify-center">
              {profile?.avatarUrl ? (
                <img
                  src={profile.avatarUrl}
                  alt={profile.displayName}
                  className="h-24 w-24 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-200 text-3xl font-semibold text-gray-600">
                  {profile?.displayName?.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <h2 className="text-xl font-semibold">{profile?.displayName}</h2>
            <p className="text-sm text-gray-600">{profile?.email}</p>
            <div className="mt-2">
              <span className={`inline-block rounded px-2 py-1 text-xs font-medium ${
                profile?.role === 'admin' ? 'bg-red-100 text-red-800' :
                profile?.role === 'editor' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {profile?.role === 'admin' ? 'Administrator' :
                 profile?.role === 'editor' ? 'Redaktør' : 'Leser'}
              </span>
            </div>
          </div>

          <div className="border-t px-6 py-4">
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500">E-post verifisert</p>
                <p className="text-sm">
                  {profile?.emailVerified ? (
                    <span className="text-green-600">✓ Verifisert</span>
                  ) : (
                    <span className="text-amber-600">⚠ Ikke verifisert</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Medlem siden</p>
                <p className="text-sm">
                  {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString('nb-NO') : '-'}
                </p>
              </div>
              {profile?.lastLoginAt && (
                <div>
                  <p className="text-xs text-gray-500">Sist innlogget</p>
                  <p className="text-sm">
                    {new Date(profile.lastLoginAt).toLocaleString('nb-NO')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Edit Profile */}
        <Card className="md:col-span-2">
          <div className="p-6">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Rediger profil</h2>
              {!isEditing && (
                <Button onClick={() => setIsEditing(true)} variant="outline">
                  Rediger
                </Button>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="displayName">Visningsnavn</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={!isEditing}
                />
              </div>

              <div>
                <Label htmlFor="email">E-post</Label>
                <Input
                  id="email"
                  value={profile?.email || ''}
                  disabled
                />
                <p className="mt-1 text-xs text-gray-500">
                  E-postadressen kan ikke endres
                </p>
              </div>

              <div>
                <Label htmlFor="avatarUrl">Avatar URL (valgfritt)</Label>
                <Input
                  id="avatarUrl"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  disabled={!isEditing}
                  placeholder="https://example.com/avatar.jpg"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Lenke til profilbilde
                </p>
              </div>

              {isEditing && (
                <div className="flex gap-2 pt-4">
                  <Button 
                    onClick={handleSave}
                    disabled={updateProfileMutation.isPending}
                  >
                    {updateProfileMutation.isPending ? 'Lagrer...' : 'Lagre endringer'}
                  </Button>
                  <Button onClick={handleCancel} variant="outline">
                    Avbryt
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
